import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { OnboardingStatusForm } from "./Form";
import { AccountType } from "../../../../../models/AccountData";
import {
  OnboardingStatus as OnboardingStatusType,
  ProjectsLocationsData,
  UpdateProjectLocationPayload,
} from "../../../../../models/ProjectData";
import { OptionalRender } from "../../../../Common/OptionalRender";
import { useProjectsLocations } from "../../hooks";

import "./styles.scss";

interface IOnboardingStatusProps {
  organizationId: string;
  projectLocation?: ProjectsLocationsData;
}

export const OnboardingStatus: React.FC<IOnboardingStatusProps> = ({
  organizationId,
  projectLocation,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `projectManagementPage.projectFacilityDetailsModal.onboardingStatus.${key}`
    );

  const [isEditMode, setIsEditMode] = useState(false);

  const { updateProjectLocation } = useProjectsLocations(
    organizationId,
    projectLocation?.project_id.toString()
  );

  const handleEdit = () => {
    setIsEditMode((v) => !v);
  };

  const handleClose = () => {
    setIsEditMode(false);
  };

  const handleSubmit = async (payload: UpdateProjectLocationPayload) => {
    await updateProjectLocation({
      projectLocationId: projectLocation?.id.toString(),
      payload,
    });
    handleClose();
  };

  const getStatusColor = (status?: OnboardingStatusType) => {
    switch (status) {
      case "INCOMPLETE":
        return "#F44336"; // Red
      case "SCHEDULED":
        return "#FF9800"; // Orange
      case "COMPLETE":
        return "#4CAF50"; // Green
      default:
        return "#9E9E9E"; // Gray
    }
  };

  const getStatusLabel = (status?: OnboardingStatusType) => {
    if (!status) return t("status_incomplete");
    return t(`status_${status.toLowerCase()}`);
  };

  const renderDateInfo = () => {
    if (
      projectLocation?.onboarding_status === "SCHEDULED" &&
      projectLocation?.onboarding_scheduled_date
    ) {
      return (
        <span className="date-info">
          {t("scheduled_date")}:{" "}
          {dayjs(projectLocation.onboarding_scheduled_date).format(
            "YYYY/MM/DD"
          )}
        </span>
      );
    }
    if (
      projectLocation?.onboarding_status === "COMPLETE" &&
      projectLocation?.onboarding_completed_date
    ) {
      return (
        <span className="date-info">
          {t("completed_date")}:{" "}
          {dayjs(projectLocation.onboarding_completed_date).format(
            "YYYY/MM/DD"
          )}
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <div className="onboarding-status">
        <div className="onboarding-status-container">
          <div className="onboarding-status-header">
            <h2 className="onboarding-status-title">
              {t("onboarding_status")}
            </h2>
            <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
              <button className="edit-button" onClick={handleEdit}></button>
            </OptionalRender>
          </div>
          <div className="onboarding-status-content">
            <span
              className="status-badge"
              style={{
                backgroundColor: getStatusColor(
                  projectLocation?.onboarding_status
                ),
              }}
            >
              {getStatusLabel(projectLocation?.onboarding_status)}
            </span>
            {renderDateInfo()}
          </div>
        </div>
      </div>
      {isEditMode && projectLocation && (
        <OnboardingStatusForm
          projectLocation={projectLocation}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
};
